// app/(routes)/solutions/ai-account-manager/components/CustomerJourneyMapComponent.tsx
'use client';

import { BarChart3, Calendar, DollarSign, MessageSquare, Phone, User } from 'lucide-react';
import Image from 'next/image';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// ============================================
// SECTION 1: PERSONALIZATION OVERVIEW
// ============================================

// Data sources / CRM integrations - using local SVGs
const dataSourcesConfig = [
  { name: 'HubSpot', icon: '/images/home_page_icons/hubspot_icon.svg' },
  { name: 'Salesforce', icon: '/images/home_page_icons/salesforce_icon.svg' },
  { name: 'Zendesk', icon: '/images/home_page_icons/zendesk_icon.svg' },
  { name: 'Intercom', icon: '/images/home_page_icons/intercom_icon.svg' },
];

// Communication channels - using brand icons where available
const channelsConfig = [
  { name: 'WhatsApp', icon: '/images/home_page_icons/whatsapp_icon.svg', type: 'svg' },
  { name: 'Telegram', icon: '/images/home_page_icons/telegram_icon.svg', type: 'svg' },
  { name: 'Instagram', icon: '/images/home_page_icons/instagram_icon.svg', type: 'svg' },
  { name: 'Email', icon: '/images/home_page_icons/gmail_icon.svg', type: 'svg' },
  { name: 'SMS', lucideIcon: MessageSquare, type: 'lucide' },
  { name: 'Phone', lucideIcon: Phone, type: 'lucide' },
];

function PersonalizationOverview() {
  return (
    <div className="mb-8 p-4  rounded-xl border border-surface-light">
      {/* Simple 3-column layout - flat design */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
        
        {/* Left: Data Sources - just icons, no white background */}
        <div>
          <h5 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-4">Data Sources</h5>
          <div className="flex items-center gap-4">
            {dataSourcesConfig.map((source) => (
              <Image 
                key={source.name}
                src={source.icon} 
                alt={source.name}
                width={32}
                height={32}
                className="object-contain"
                title={source.name}
              />
            ))}
            <span className="text-sm text-stone-500">+ your sources</span>
          </div>
        </div>
        
        {/* Middle: Customer Profile - clean inline */}
        <div>
          <h5 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-4">Live Customer Data</h5>
          
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-stone-200 to-stone-300 rounded-full flex items-center justify-center flex-shrink-0">
              <User size={20} className="text-stone-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-base font-semibold text-black">Sarah K.</span>
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Expansion</span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-stone-600">
                <span className="flex items-center gap-1">
                  <DollarSign size={14} />
                  $142k
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 size={14} />
                  87 score
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  2d ago
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right: Channels - just icons, no white background */}
        <div>
          <h5 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-4">Channels</h5>
          <div className="flex items-center gap-3">
            {channelsConfig.map((channel) => (
              channel.type === 'svg' && channel.icon ? (
                <Image 
                  key={channel.name}
                  src={channel.icon} 
                  alt={channel.name}
                  width={28}
                  height={28}
                  className="object-contain"
                  title={channel.name}
                />
              ) : channel.lucideIcon ? (
                <span key={channel.name} title={channel.name}>
                  <channel.lucideIcon size={28} className="text-stone-600" />
                </span>
              ) : null
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SECTION 2: JOURNEY STAGE CARDS
// ============================================

interface JourneyStep {
  timing: string;
  action: string;
  channel: 'sms' | 'whatsapp' | 'email' | 'telegram' | 'call' | 'instagram';
}

interface JourneyStage {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  trigger: string;
  accentColor: string;
  badgeColor: string;
  steps: JourneyStep[];
}

const channelIconConfig: Record<JourneyStep['channel'], { icon: string; label: string; color: string }> = {
  sms: { icon: '', label: 'SMS', color: 'bg-green-100 text-green-700 border-green-200' },
  whatsapp: { icon: '/images/home_page_icons/whatsapp_icon.svg', label: 'WhatsApp', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  email: { icon: '/images/home_page_icons/gmail_icon.svg', label: 'Email', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  telegram: { icon: '/images/home_page_icons/telegram_icon.svg', label: 'Telegram', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  instagram: { icon: '/images/home_page_icons/instagram_icon.svg', label: 'Instagram', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  call: { icon: '', label: 'Call', color: 'bg-purple-100 text-purple-700 border-purple-200' },
};

const journeyStages: JourneyStage[] = [
  {
    id: 'activation',
    title: 'FTD Booster',
    subtitle: 'New clients feel lost. The AI acts as a personal concierge, guiding them document-by-document until the account is fully funded.',
    icon: '📋',
    trigger: 'Signup Complete, KYC Pending',
    accentColor: 'bg-orange-500',
    badgeColor: 'bg-orange-500',
    steps: [
      { timing: 'Instant', action: 'Welcome + document checklist', channel: 'whatsapp' },
      { timing: 'T+4h', action: 'Step-by-step upload guide', channel: 'sms' },
      { timing: 'T+24h', action: 'Status update + clear next steps', channel: 'email' },
      { timing: 'T+3d', action: 'Offer personal onboarding call', channel: 'call' },
      { timing: 'T+7d', action: 'First deposit incentive', channel: 'telegram' },
    ],
  },
  {
    id: 'engagement',
    title: 'LTV Maximizer',
    subtitle: 'Keeps your brand top-of-mind with hyper-relevant content, ensuring you are the first place they look when investing more capital.',
    icon: '💬',
    trigger: 'Active Account, Funded',
    accentColor: 'bg-blue-500',
    badgeColor: 'bg-blue-500',
    steps: [
      { timing: 'Weekly', action: 'Personalized performance digest', channel: 'email' },
      { timing: 'On Event', action: 'Market opportunity alert', channel: 'whatsapp' },
      { timing: 'Monthly', action: 'Account health summary', channel: 'sms' },
    ],
  },
  {
    id: 'retention',
    title: 'Churn Radar',
    subtitle: 'When a client goes silent, the AI reaches out with a low-pressure, service-first approach: "Is everything okay with your account?"',
    icon: '🛡️',
    trigger: '14+ Days Inactive',
    accentColor: 'bg-purple',
    badgeColor: 'bg-purple',
    steps: [
      { timing: 'Day 1', action: 'Friendly check-in (no pitch)', channel: 'whatsapp' },
      { timing: 'Day 5', action: 'Share relevant opportunity', channel: 'email' },
      { timing: 'Day 10', action: 'Exclusive re-engagement offer', channel: 'sms' },
      { timing: 'Day 14', action: 'Personal advisor outreach', channel: 'call' },
    ],
  },
  {
    id: 'expansion',
    title: 'Share of Wallet Hunter',
    subtitle: 'Systematically identifies gaps in a client\'s portfolio and cross-sells the exact product needed to fill them.',
    icon: '📈',
    trigger: 'High Engagement Score',
    accentColor: 'bg-green-500',
    badgeColor: 'bg-green-500',
    steps: [
      { timing: 'On Signal', action: 'Contextual upsell opportunity', channel: 'email' },
      { timing: 'T+3d', action: 'Educational content on new products', channel: 'whatsapp' },
      { timing: 'Monthly', action: 'Referral program invite', channel: 'instagram' },
    ],
  },
];

function JourneyStageCard({ stage }: { stage: JourneyStage }) {
  return (
    <div className="relative bg-white rounded-xl overflow-hidden h-full border border-stone-200 shadow-sm flex flex-col">
      {/* Top accent bar */}
      <div className={cn('h-1.5 flex-shrink-0', stage.accentColor)} />
      
      {/* Badge */}
      <div className="absolute top-4 right-4 z-10">
        <Badge className={cn('text-xs text-white font-medium px-2 py-1', stage.badgeColor)}>
          {stage.id === 'activation' ? 'Activation' : 
           stage.id === 'engagement' ? 'Engagement' : 
           stage.id === 'retention' ? 'Retention' : 'Expansion'}
        </Badge>
      </div>
      
      <div className="p-5 pt-10 flex flex-col flex-1">
        {/* Title - fixed height */}
        <h3 className="text-lg font-bold text-black mb-2 pr-20 h-[52px]">{stage.title}</h3>
        
        {/* Subtitle - fixed height to ensure alignment */}
        <p className="text-sm text-text-secondary h-[60px] overflow-hidden">{stage.subtitle}</p>
        
        {/* Trigger - now starts at consistent position */}
        <div className="py-3 border-b border-stone-100 mt-2">
          <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Trigger</span>
          <p className="text-sm font-medium text-black mt-1">{stage.trigger}</p>
        </div>
        
        {/* Timeline - starts at consistent position */}
        <div className="relative pl-5 pt-4">
          {/* Vertical line */}
          <div className="absolute left-[4px] top-2 bottom-2 w-[2px] bg-stone-200" />
          
          {/* Steps */}
          <div className="space-y-2.5">
            {stage.steps.map((step, idx) => {
              const channelConfig = channelIconConfig[step.channel];
              const isFirst = idx === 0;
              return (
                <div key={idx} className="relative">
                  {/* Timeline dot */}
                  <div className={cn(
                    'absolute -left-5 top-1 w-2.5 h-2.5 rounded-full z-10',
                    isFirst ? 'bg-stone-700' : 'bg-white border-2 border-stone-300'
                  )} />
                  
                  {/* Content */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-stone-500 w-16 flex-shrink-0">{step.timing}</span>
                      <span className={cn(
                        'inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border',
                        channelConfig.color
                      )}>
                        {channelConfig.icon ? (
                          <Image src={channelConfig.icon} alt={channelConfig.label} width={12} height={12} className="object-contain" />
                        ) : step.channel === 'sms' ? (
                          <MessageSquare size={10} />
                        ) : (
                          <Phone size={10} />
                        )}
                        {channelConfig.label}
                      </span>
                    </div>
                    {/* Commented out - action text was crowding the cards */}
                    {/* <p className="text-sm text-stone-600 mt-0.5">{step.action}</p> */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================

function AIIntelligenceHub() {
  return (
    <div className="relative py-6 my-2">
      <div className="flex items-center justify-center gap-4">
        {/* Left line */}
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-stone-300 to-black" />
        
        {/* Central badge */}
        <div className="flex items-center gap-2 px-5 py-2.5 bg-black rounded-full shadow-lg">
          <div className="w-2.5 h-2.5 bg-gradient-to-br from-orange-400 to-green-400 rounded-full animate-pulse" />
          <span className="text-white font-semibold text-sm tracking-wide">AI Account Manager</span>
        </div>
        
        {/* Right line */}
        <div className="flex-1 h-px bg-gradient-to-l from-transparent via-stone-300 to-black" />
      </div>
      
      <p className="text-center text-sm text-stone-500 mt-3">
        One intelligence powers every touchpoint
      </p>
    </div>
  );
}

export function CustomerJourneyMapComponent() {
  return (
    <div className="w-full">
      {/* Personalization Overview - Clean, no nested cards */}
      <PersonalizationOverview />
      
      {/* AI Intelligence Hub - connects data to sequences */}
      <AIIntelligenceHub />
      
      {/* Journey Stage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
        {journeyStages.map((stage) => (
          <JourneyStageCard key={stage.id} stage={stage} />
        ))}
      </div>
    </div>
  );
}
